import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { MetricDefinitionId } from '@/application/metrics/metricDefinitions';
import { Icon } from '@/presentation/components/common/Icon';
import { MetricDefinitionTooltip } from './MetricDefinitionTooltip';
import type { MetricTone } from './types';

/** Jerarquía visual de KPIs: hero > primary > compact (detalle denso). */
export type MetricKpiVariant = 'hero' | 'primary' | 'compact';

interface MetricKpiCardProps {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  tone?: MetricTone;
  variant?: MetricKpiVariant;
  /** Si se pasa, muestra un ojo que abre el detalle de la tarjeta. */
  onDetail?: () => void;
  /** Id del glosario (`metrics.glossary.*`). Muestra “?” reutilizable. */
  metricId?: MetricDefinitionId;
  'data-testid'?: string;
  'data-kpi-id'?: string;
}

const toneClass: Record<MetricTone, string> = {
  blue: 'metric-kpi--tone-blue',
  green: 'metric-kpi--tone-green',
  amber: 'metric-kpi--tone-amber',
  red: 'metric-kpi--tone-red',
  indigo: 'metric-kpi--tone-indigo',
  slate: 'metric-kpi--tone-slate',
};

function valueLength(value: ReactNode): number {
  return typeof value === 'string' ? value.length : 0;
}

/** Auto-ajuste solo en compact: valores largos reducen fuente. */
function compactValueSizeClass(value: ReactNode): string {
  const length = valueLength(value);
  if (length > 11) return 'metric-kpi__value--long';
  if (length > 8) return 'metric-kpi__value--medium';
  return '';
}

export function MetricKpiCard({
  label,
  value,
  detail,
  tone = 'slate',
  variant = 'compact',
  onDetail,
  metricId,
  'data-testid': testId,
  'data-kpi-id': kpiId,
}: MetricKpiCardProps) {
  const { t } = useTranslation();
  const compactExtra = variant === 'compact' ? compactValueSizeClass(value) : '';

  return (
    <article
      role={variant === 'primary' ? 'listitem' : undefined}
      data-testid={testId}
      data-kpi-id={kpiId}
      data-metric-kpi-variant={variant}
      className={`metric-kpi metric-kpi--${variant} ${toneClass[tone]}`}
    >
      <div className="metric-kpi__head">
        <div className="flex min-w-0 items-center gap-1">
          <p className="metric-kpi__label">{label}</p>
          {metricId ? (
            <MetricDefinitionTooltip metricId={metricId} size="sm" align="end" />
          ) : null}
        </div>
        {onDetail && (
          <button
            type="button"
            onClick={onDetail}
            title={t('metrics.detail.view')}
            aria-label={t('metrics.detail.view')}
            className="metric-kpi__detail-btn"
          >
            <Icon path="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12zM15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" className="h-4 w-4" />
          </button>
        )}
      </div>
      <p className={`metric-kpi__value bi-num ${compactExtra}`}>{value}</p>
      {detail ? <p className="metric-kpi__detail">{detail}</p> : null}
    </article>
  );
}
